import { Component, ElementRef, OnDestroy, TemplateRef, ViewContainerRef, ViewEncapsulation, ChangeDetectionStrategy, inject, output, viewChild, input } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { SbModalService } from '../sb-modal/sb-modal.service';
import { NgTemplateOutlet } from '@angular/common';

export type SbImagePreviewTrigger = 'hover' | 'click' | 'both';
export type SbImagePreviewFit = 'contain' | 'cover';

const VIEWPORT_MARGIN = 16;
const GAP = 12;

@Component({
  selector: 'app-sb-image-preview',
  templateUrl: './sb-image-preview.component.html',
  styleUrls: ['./sb-image-preview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet]
})
export class SbImagePreviewComponent implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly modal = inject(SbModalService);

  readonly src = input('');
  /** The full-size image to preview; the thumbnail's `src` when empty. */
  readonly previewSrc = input('');
  readonly alt = input('');
  readonly caption = input('');
  /** Title of the click preview; the caption, then the alt text, when empty. */
  readonly previewTitle = input('');
  readonly trigger = input<SbImagePreviewTrigger>('both');
  readonly width = input(150);
  readonly height = input(150);
  readonly fit = input<SbImagePreviewFit>('contain');
  readonly loading = input<'lazy' | 'eager'>('lazy');

  readonly loadError = output<string>();

  readonly thumbnail = viewChild<ElementRef<HTMLElement>>('thumbnail');
  readonly hoverPreview = viewChild<TemplateRef<unknown>>('hoverPreview');
  readonly clickPreview = viewChild<TemplateRef<unknown>>('clickPreview');

  failed = false;
  hoverMaxWidth = 0;
  hoverMaxHeight = 0;
  private hoverRef: OverlayRef | null = null;

  get fullSrc(): string {
    return this.previewSrc() || this.src();
  }

  get hoverEnabled(): boolean {
    return !this.failed && !!this.fullSrc && this.trigger() !== 'click';
  }

  get clickEnabled(): boolean {
    return !this.failed && !!this.fullSrc && this.trigger() !== 'hover';
  }

  get label(): string {
    return this.previewTitle() || this.caption() || this.alt() || 'Image preview';
  }

  showHover(): void {
    const origin = this.thumbnail()?.nativeElement;
    const hoverPreview = this.hoverPreview();
    if (!this.hoverEnabled || this.hoverRef || !origin || !hoverPreview) {
      return;
    }
    const rect = origin.getBoundingClientRect();
    const viewport = document.documentElement;
    const roomRight = viewport.clientWidth - rect.right - GAP - 2 * VIEWPORT_MARGIN;
    const roomLeft = rect.left - GAP - 2 * VIEWPORT_MARGIN;
    const right: ConnectedPosition = {
      originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: GAP,
    };
    const left: ConnectedPosition = {
      originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -GAP,
    };
    this.hoverMaxWidth = Math.max(roomRight, roomLeft, 0);
    this.hoverMaxHeight = Math.max(viewport.clientHeight - 3 * VIEWPORT_MARGIN, 0);
    const hoverRef = this.overlay.create({
      positionStrategy: this.overlay.position()
        .flexibleConnectedTo(origin)
        .withPositions(roomRight >= roomLeft ? [right, left] : [left, right])
        .withViewportMargin(VIEWPORT_MARGIN)
        .withPush(true),
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: 'sb-image-preview__hover-panel',
    });
    this.hoverRef = hoverRef;
    hoverRef.detachments().subscribe(() => {
      if (this.hoverRef === hoverRef) {
        this.hideHover();
      }
    });
    hoverRef.attach(new TemplatePortal(hoverPreview, this.viewContainerRef));
  }

  repositionHover(): void {
    this.hoverRef?.updatePosition();
  }

  hideHover(): void {
    const hoverRef = this.hoverRef;
    this.hoverRef = null;
    hoverRef?.dispose();
  }

  openPreview(): void {
    const clickPreview = this.clickPreview();
    if (!this.clickEnabled || !clickPreview) {
      return;
    }
    this.hideHover();
    this.modal.open({
      title: this.label,
      bodyTemplate: clickPreview,
      size: 'xl',
      externalUrl: this.fullSrc,
      externalLabel: 'Open the image in a new tab',
      windowClass: 'sb-image-preview-modal',
      showFooter: false,
    });
  }

  onError(): void {
    this.failed = true;
    this.hideHover();
    this.loadError.emit(this.src());
  }

  ngOnDestroy(): void {
    this.hideHover();
  }
}
